import { differenceInDays } from 'date-fns';
import { initializeApp } from 'firebase/app';
import { Messaging, getMessaging, getToken } from 'firebase/messaging';
import { isInIframe, isNative } from '../../../shared/utils/is';
import { UserDevice } from '../../shared/forms';
import { firebaseConfig } from './config';

const DEVICE_ID_KEY = 'push_notifications_device_id';
const LAST_PUSH_TIMESTAMP_KEY = 'last_push_notification_registration';
const VAPID_KEY = process.env.FIREBASE_VAPID_KEY;

const setSetting = async (key, value) => {
    return new Promise((resolve) => {
        $f.mobile.settings.set(key, value, resolve);
    });
};

const getSetting = async (key) => {
    return new Promise<string>((resolve) => {
        $f.mobile.settings.get(key, resolve);
    });
};

const getDeviceId = async () => {
    return await getSetting(DEVICE_ID_KEY);
};

const setDeviceId = async (id) => {
    await setSetting(DEVICE_ID_KEY, id);
    await setSetting(LAST_PUSH_TIMESTAMP_KEY, new Date());
};

const registerWithZenput = async (pushToken) => {
    const device = new UserDevice({
        id: pushToken,
        name: $f.mobile.settings.get_platform_type('Unknown', true)
    });
    return new Promise<void>((resolve) => {
        device.save(
            {},
            {
                success: async () => {
                    await setDeviceId(pushToken);
                    resolve();
                },
                error: () => {
                    // @author:david nothing we can do - device registration will retry the next time the app is opened
                    resolve();
                }
            }
        );
    });
};

const getLastRegistration = async () => {
    const dateString: string = await getSetting(LAST_PUSH_TIMESTAMP_KEY);
    if (!dateString) {
        return null;
    }
    return new Date(dateString);
};

const isEligibleForRegistration = async () => {
    if (!$f.active_user.is_logged_in()) {
        return false;
    }

    const lastRegistration = await getLastRegistration();
    if (!lastRegistration) {
        return true;
    }

    return differenceInDays(new Date(), lastRegistration) >= 1;
};

export const requestPermission = async (messaging: Messaging) => {
    if (!(await isEligibleForRegistration())) {
        return;
    }

    const permission = await Notification.requestPermission();
    if (permission === 'granted') {
        try {
            const currentToken = await getToken(messaging, {
                vapidKey: VAPID_KEY,
                serviceWorkerRegistration: await navigator.serviceWorker.getRegistration()
            });
            if (currentToken) {
                await registerWithZenput(currentToken);
            } else {
                console.error('No registration token available. Request permission to generate one.');
            }
        } catch (err) {
            console.error('An error occurred while retrieving token: ', err);
        }
    } else {
        console.warn('Unable to get permission to notify.');
    }
};

export const initializePushNotificationsForPWA = async () => {
    if (isNative() || !('serviceWorker' in navigator) || isInIframe()) {
        return;
    }

    try {
        const app = initializeApp(firebaseConfig);

        const messaging = getMessaging(app);

        await requestPermission(messaging);
    } catch (e) {
        $f.mobile.logging.log_error('Failed to start firebase messaging', {
            error: e
        });
    }
};

export const unregisterDeviceForPWA = async () => {
    if (isNative()) {
        return;
    }

    const deviceId = await getDeviceId();
    const device = new UserDevice({ id: deviceId });

    device.destroy();

    await setSetting(DEVICE_ID_KEY, null);
    await setSetting(LAST_PUSH_TIMESTAMP_KEY, null);
};
